import { matchPath } from 'react-router-dom';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from 'react-apollo';
import TagManager from 'react-gtm-module';

import useThrottle from '../../../../../../hooks/useThrottle';
import { getAccountQuery } from '../../../../graphql/queries/getAccount';
import { getAlbumMembershipGroupFunnelDataQuery } from '../graphql/queries/getAlbumMembershipGroupFunnelData';
import {
  albumFolderIsPrePaid,
  getAlbumFolderDiscountDescriptor,
  getAlbumFolderPaymentDescriptor,
} from '../../../../../../util/album-folder';
import useConsentManager from '../../../../../../hooks/useConsentManager';

const getAlbumMembershipGroupIdParam = pathname => {
  const paths = [
    '/consumer/matches/:albumMembershipGroupId',
    '/consumer/matches/:albumMembershipGroupId/all',
    '/consumer/matches/:albumMembershipGroupId/checkout',
  ];
  const current = paths.find(
    path => !!matchPath(pathname, { path, exact: true }),
  );
  const matchProfile = matchPath(pathname, { path: current, exact: true });

  return matchProfile?.params ?? {};
};

/**
 * Hook responsible for firing an event every ~30sec (based on location change)
 * which provides GTM (google analytics) with tagging information about
 * the gallery a user is currently visiting.
 *
 * @param {Object} location React router's location.
 * @param {string} location.pathname The pathname.
 */
const useMembershipsGTMDimensions = location => {
  const authenticated = useSelector(({ auth }) => auth.authenticated);
  const pathname = useThrottle(location.pathname, 30000);
  const { albumMembershipGroupId } = getAlbumMembershipGroupIdParam(pathname);
  const { hasNonEssentialConsent } = useConsentManager();
  const { data: { me: user } = {} } = useQuery(getAccountQuery, {
    skip: !authenticated,
  });
  const { data: { albumMembershipGroup } = {} } = useQuery(
    getAlbumMembershipGroupFunnelDataQuery,
    {
      variables: {
        id: albumMembershipGroupId,
      },
      skip: !authenticated || !albumMembershipGroupId,
    },
  );

  const albumFolder =
    albumMembershipGroup?.album?.root_album_folder ||
    albumMembershipGroup?.album_folder;

  useEffect(() => {
    if (!albumFolder || !user) {
      return;
    }

    if (!hasNonEssentialConsent) {
      return;
    }

    TagManager.dataLayer({
      dataLayer: {
        event: 'newEvent',
        newEventData: { type: 'galleryTagging' },
        newSessionData: {
          galleryId: albumFolder.id,
          gallerySubType: albumFolder.album_subtype || 'GENERAL',
          galleryPaymentType: albumFolderIsPrePaid(albumFolder)
            ? 'Pre-paid'
            : 'Consumer Pay',
          galleryPaymentUnit: getAlbumFolderPaymentDescriptor(albumFolder),
          galleryPaymentDiscount: getAlbumFolderDiscountDescriptor(albumFolder),
          userHasMemberships: true,
          userHasGalleryMembership: true,
        },
      },
    });
  }, [albumFolder?.id, user?.id, pathname, hasNonEssentialConsent]);
};

export default useMembershipsGTMDimensions;
